import { useEffect, useMemo, useState } from 'react'
import api from '../../../api'
import { CursoModel } from '../../../models/CursoModel'

export type PortalEditorOption<T> = {
  value: T
  label: string
}

export function useSiteCourseOptions(selectedCourseIds: number[] = []) {
  const [cursosCatalogo, setCursosCatalogo] = useState<CursoModel[]>([])
  const [loadingCursos, setLoadingCursos] = useState(false)

  useEffect(() => {
    let isMounted = true

    async function fetchCursos() {
      setLoadingCursos(true)
      try {
        const rows = (await api.Cursos.getAll({ includeSiteMetadata: true })) || []
        const cursosDisponiveisNoSite = rows
          .filter((curso) => Boolean(curso.siteMetadata))
          .sort((firstCurso, secondCurso) => (firstCurso.id || 0) - (secondCurso.id || 0))

        if (isMounted) setCursosCatalogo(cursosDisponiveisNoSite)
      } catch (error) {
        console.error('Error fetching cursos:', error)
      } finally {
        if (isMounted) setLoadingCursos(false)
      }
    }

    void fetchCursos()

    return () => {
      isMounted = false
    }
  }, [])

  const courseOptions = useMemo(() => {
    const catalogOptions = cursosCatalogo.map((curso) => ({
      value: curso.id,
      label: `${curso.nomeReduzido || curso.nome} (#${curso.id})`,
    }))
    const catalogIds = new Set(catalogOptions.map((option) => option.value))
    const missingOptions = selectedCourseIds
      .filter((courseId) => !catalogIds.has(courseId))
      .map((courseId) => ({
        value: courseId,
        label: `Curso #${courseId}`,
      }))

    return [...catalogOptions, ...missingOptions]
  }, [cursosCatalogo, selectedCourseIds])

  return {
    courseOptions,
    loadingCursos,
  }
}

export function usePortalPostOptions(
  selectedSlugs: string[] = [],
  currentArticle?: { id?: number; slug?: string }
) {
  const [portalArtigos, setPortalArtigos] = useState<Array<{ id?: number; titulo: string; slug: string }>>([])
  const [loadingArtigos, setLoadingArtigos] = useState(false)

  useEffect(() => {
    let isMounted = true

    async function fetchPortalArtigos() {
      setLoadingArtigos(true)
      try {
        const artigosResponse = await api.PortalArtigos.get({
          limit: 100,
          offset: 0,
          order: 'ASC',
          orderBy: 'titulo',
          status: 'PUBLICADO',
        })

        if (isMounted) setPortalArtigos(artigosResponse?.rows || [])
      } catch (error) {
        console.error('Error fetching portal artigos:', error)
      } finally {
        if (isMounted) setLoadingArtigos(false)
      }
    }

    void fetchPortalArtigos()

    return () => {
      isMounted = false
    }
  }, [])

  const portalPostOptions = useMemo(
    () => {
      const catalogOptions = portalArtigos
        .filter(
          (artigo) =>
            artigo.id !== currentArticle?.id && artigo.slug !== currentArticle?.slug
        )
        .map((artigo) => ({
          value: artigo.slug,
          label: `${artigo.titulo} (/${artigo.slug})`,
        }))
      const catalogSlugs = new Set(catalogOptions.map((option) => option.value))
      const missingOptions = selectedSlugs
        .filter(
          (slug) => slug !== currentArticle?.slug && !catalogSlugs.has(slug)
        )
        .map((slug) => ({ value: slug, label: `Artigo /${slug}` }))

      return [...catalogOptions, ...missingOptions]
    },
    [currentArticle?.id, currentArticle?.slug, portalArtigos, selectedSlugs]
  )

  return {
    portalPostOptions,
    loadingArtigos,
  }
}
